/**
 * ToolConfig Component (Task 6.4, 6.6, 6.7, 6.8)
 *
 * Main container for tool configuration that integrates:
 * - ToolList for displaying and grouping tools
 * - ToolForm for creating and editing tools
 * - Tool deletion with timeline warning
 * - Tool duplication
 *
 * Features:
 * - Tool creation flow with form
 * - Tool editing with pre-filled form
 * - Tool deletion with timeline usage check
 * - Tool duplication with "(copy)" suffix
 */

import { useState, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { usePuppetStore } from '@/stores/puppetStore';
import { useTimelineStore } from '@/stores/timelineStore';
import type {
  ToolDefinition,
  BodyPart,
  EasingFunction,
  RotationAxis,
} from '@/stores/toolStore';
import { ToolList } from './ToolList';
import { ToolForm } from './ToolForm';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';

interface ToolFormData {
  name: string;
  bodyPart: BodyPart;
  axis: RotationAxis;
  minDegrees: number;
  maxDegrees: number;
  dockingPosition: number;
  startingPosition: number;
  defaultDuration: number;
  defaultEasing: EasingFunction;
  color: string;
  icon?: string;
  httpEndpoints?: {
    start?: string;
    stop?: string;
  };
}

interface ToolConfigProps {
  /**
   * The puppet ID whose tools are being configured
   */
  puppetId: string;
}

export function ToolConfig({ puppetId }: ToolConfigProps) {
  // Store actions
  const addTool = usePuppetStore((state) => state.addTool);
  const updateTool = usePuppetStore((state) => state.updateTool);
  const deleteTool = usePuppetStore((state) => state.deleteTool);
  const duplicateTool = usePuppetStore((state) => state.duplicateTool);

  // Get timeline tools to check for usage
  const timelineTools = useTimelineStore((state) => state.tools);

  // UI state
  const [showForm, setShowForm] = useState(false);
  const [editingTool, setEditingTool] = useState<ToolDefinition | null>(null);
  const [toolToDelete, setToolToDelete] = useState<ToolDefinition | null>(null);

  // Check if a tool is used in the timeline
  const _getToolTimelineUsage = (toolId: string): number => {
    return timelineTools.filter((t) => t.toolId === toolId).length;
  };

  // Memoize timeline usage for the tool being deleted
  const deleteToolUsage = useMemo(() => {
    if (!toolToDelete) return 0;
    // Inline the check to satisfy eslint dependencies
    return timelineTools.filter((t) => t.toolId === toolToDelete.id).length;
  }, [toolToDelete, timelineTools]);

  // Handle adding a new tool
  const handleAddTool = () => {
    setEditingTool(null);
    setShowForm(true);
  };

  // Handle editing a tool
  const handleEditTool = (tool: ToolDefinition) => {
    setEditingTool(tool);
    setShowForm(true);
  };

  // Handle delete request (shows confirmation dialog)
  const handleDeleteRequest = (tool: ToolDefinition) => {
    setToolToDelete(tool);
  };

  // Handle duplicate tool
  const handleDuplicateTool = (tool: ToolDefinition) => {
    duplicateTool(puppetId, tool.id);
  };

  // Handle form submission
  const handleFormSubmit = (data: ToolFormData) => {
    if (editingTool) {
      // Update existing tool
      updateTool(puppetId, editingTool.id, {
        name: data.name,
        bodyPart: data.bodyPart,
        axis: data.axis,
        minDegrees: data.minDegrees,
        maxDegrees: data.maxDegrees,
        dockingPosition: data.dockingPosition,
        startingPosition: data.startingPosition,
        defaultDuration: data.defaultDuration,
        defaultEasing: data.defaultEasing,
        color: data.color,
        icon: data.icon,
        httpEndpoints: data.httpEndpoints,
      });
    } else {
      // Add new tool
      addTool(puppetId, {
        name: data.name,
        bodyPart: data.bodyPart,
        axis: data.axis,
        minDegrees: data.minDegrees,
        maxDegrees: data.maxDegrees,
        dockingPosition: data.dockingPosition,
        startingPosition: data.startingPosition,
        defaultDuration: data.defaultDuration,
        defaultEasing: data.defaultEasing,
        color: data.color,
        isPrebuilt: false,
        icon: data.icon,
        httpEndpoints: data.httpEndpoints,
      });
    }

    setShowForm(false);
    setEditingTool(null);
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTool(null);
  };

  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (toolToDelete) {
      // Remove tool from timeline first if used
      if (deleteToolUsage > 0) {
        const toolsToRemove = timelineTools.filter(
          (t) => t.toolId === toolToDelete.id
        );
        toolsToRemove.forEach((t) => {
          useTimelineStore.getState().removeTool(t.id);
        });
      }

      // Delete the tool definition
      deleteTool(puppetId, toolToDelete.id);
      setToolToDelete(null);
    }
  };

  // Handle delete cancel
  const handleCancelDelete = () => {
    setToolToDelete(null);
  };

  // Build delete confirmation message
  const getDeleteMessage = (): string => {
    if (!toolToDelete) return '';

    const baseMessage = `Are you sure you want to delete "${toolToDelete.name}"?`;

    if (deleteToolUsage > 0) {
      const usageText =
        deleteToolUsage === 1
          ? '1 block on the timeline uses this tool'
          : `${deleteToolUsage} blocks on the timeline use this tool`;
      return `${baseMessage}\n\nWarning: ${usageText}. Deleting this tool will also remove those blocks.`;
    }

    return `${baseMessage} This action cannot be undone.`;
  };

  if (!puppetId) {
    return (
      <div
        className="flex h-full items-center justify-center p-4 text-sm"
        style={{ color: 'var(--color-dark-text-muted)' }}
      >
        No puppet selected
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full flex-col">
        <AnimatePresence mode="wait">
          {showForm ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex-1 overflow-y-auto p-4"
            >
              <ToolForm
                tool={editingTool || undefined}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                isEditing={!!editingTool}
              />
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 overflow-hidden"
            >
              <ToolList
                puppetId={puppetId}
                onAddTool={handleAddTool}
                onEditTool={handleEditTool}
                onDeleteTool={handleDeleteRequest}
                onDuplicateTool={handleDuplicateTool}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Delete Confirmation Dialog (Task 6.7) */}
      <ConfirmDialog
        open={!!toolToDelete}
        onOpenChange={(open) => !open && handleCancelDelete()}
        title="Delete Tool"
        description={getDeleteMessage()}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        destructive
      />
    </>
  );
}
